import { useCallback, useEffect, useMemo, useState } from "react";
import { Inbox, Loader2, MapPin, MessageCircle, Paperclip, RefreshCw, Search, Settings as SettingsIcon } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { api } from "../lib/api";

const CLASSIFICATION = {
  rapportino: { label: "Rapportino", cls: "bg-orange-50 text-orange-700 border-orange-200" },
  ddt: { label: "DDT", cls: "bg-blue-50 text-blue-700 border-blue-200" },
  fattura: { label: "Fattura", cls: "bg-violet-50 text-violet-700 border-violet-200" },
  presenze: { label: "Presenze", cls: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  altro: { label: "Altro", cls: "bg-slate-50 text-slate-600 border-slate-200" },
};

const TARGET = { journal: "Giornale lavori", site_costs: "Contabilità", attendance: "Presenze", inbox: "Da verificare" };

const fmtDate = (value) => {
  if (!value) return "—";
  const n = Number(value);
  const d = Number.isFinite(n) ? new Date(n * 1000) : new Date(value);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString("it-IT");
};

export default function WhatsAppInbox() {
  const [status, setStatus] = useState(null);
  const [mode, setMode] = useState("chat");
  const [channels, setChannels] = useState([]);
  const [selectedChat, setSelectedChat] = useState("");
  const [chatMessages, setChatMessages] = useState([]);
  const [messages, setMessages] = useState([]);
  const [sites, setSites] = useState([]);
  const [siteId, setSiteId] = useState("");
  const [tab, setTab] = useState("tutte");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [syncBusy, setSyncBusy] = useState(false);
  const [chatBusy, setChatBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const statusParam = tab === "da_verificare" ? "needs_review" : tab === "elaborate" ? "processed" : undefined;
      const [st, rows, ss, ch] = await Promise.all([
        api.whatsappStatus(),
        api.whatsappInbox({ site_id: siteId || undefined, status: statusParam, limit: 500 }),
        api.listSites(),
        api.whatsappChannels().catch(() => []),
      ]);
      setStatus(st);
      setMessages(rows || []);
      setSites(ss || []);
      const activeChannels = (ch || []).filter((x) => x.enabled && ((x.site_ids || []).length > 0 || x.site_id || x.company_communications));
      setChannels(activeChannels);
      if (!selectedChat && activeChannels.length) setSelectedChat(activeChannels[0].chat_id);
      else if (selectedChat && !activeChannels.some((x) => x.chat_id === selectedChat)) setSelectedChat(activeChannels[0]?.chat_id || "");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Caricamento WhatsApp non riuscito");
    } finally {
      setLoading(false);
    }
  }, [siteId, tab, selectedChat]);

  const loadChat = useCallback(async (chatId) => {
    if (!chatId) { setChatMessages([]); return; }
    setChatBusy(true);
    try { setChatMessages(await api.whatsappMessages(chatId, 80)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Lettura chat WhatsApp non riuscita"); }
    finally { setChatBusy(false); }
  }, []);

  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  useEffect(() => { if (selectedChat) loadChat(selectedChat); }, [selectedChat, loadChat]);

  const sync = async () => {
    setSyncBusy(true);
    try {
      const r = await api.whatsappSync();
      toast.success(`WhatsApp: ${r.new || 0} nuovi · ${r.needs_review || 0} da verificare · ${r.errors || 0} errori`);
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Sincronizzazione WhatsApp non riuscita"); }
    finally { setSyncBusy(false); }
  };

  const mapChat = async (chatId, newSiteId) => {
    if (!newSiteId) return;
    try {
      await api.whatsappMapChannel(chatId, { site_id: newSiteId, enabled: true, include_history: false });
      toast.success("Chat associata al cantiere");
      await load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Associazione non riuscita"); }
  };

  const siteById = useMemo(() => Object.fromEntries(sites.map((s) => [s.id, s])), [sites]);
  const selected = channels.find((c) => c.chat_id === selectedChat);
  const filteredChannels = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return channels;
    return channels.filter((c) => [c.chat_name, c.chat_id, siteById[c.site_id]?.name].some((v) => (v || "").toLowerCase().includes(needle)));
  }, [channels, q, siteById]);

  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return messages;
    return messages.filter((m) =>
      [m.chat_name, m.body, m.transcript, m.summary, m.classification, siteById[m.site_id]?.name]
        .some((v) => (v || "").toLowerCase().includes(needle))
    );
  }, [messages, q, siteById]);

  return <div className="space-y-5">
    <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-3">
      <div>
        <div className="flex items-center gap-2"><MessageCircle className="w-7 h-7 text-emerald-600" /><h1 className="text-3xl font-display font-extrabold text-slate-900">WhatsApp</h1></div>
        <p className="text-sm text-slate-500 mt-1">Lettura chat WhatsApp e inbox operativa dei cantieri.</p>
      </div>
      <div className="flex gap-2">
        <button onClick={load} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg border border-slate-200 bg-white text-sm font-semibold disabled:opacity-50">{loading?<Loader2 className="w-4 h-4 animate-spin"/>:<RefreshCw className="w-4 h-4"/>}Aggiorna</button>
        {status?.status === "ready" && <button onClick={sync} disabled={syncBusy} className="inline-flex items-center gap-2 px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-semibold disabled:opacity-50">{syncBusy?<Loader2 className="w-4 h-4 animate-spin"/>:<RefreshCw className="w-4 h-4"/>}Sincronizza</button>}
      </div>
    </div>

    {status?.status === "disabled" && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 flex flex-col sm:flex-row sm:items-center gap-3"><span className="flex-1">WhatsApp è disabilitato nelle Impostazioni.</span><Link to="/impostazioni" className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-red-600 text-white font-semibold"><SettingsIcon className="w-4 h-4"/>Apri Impostazioni</Link></div>}

    <div className="inline-flex bg-slate-100 rounded-lg p-1">
      <button onClick={()=>setMode("chat")} className={"px-4 py-2 rounded-md text-sm font-semibold "+(mode==="chat"?"bg-white shadow-sm":"text-slate-500")}>Chat WhatsApp</button>
      <button onClick={()=>setMode("inbox")} className={"px-4 py-2 rounded-md text-sm font-semibold "+(mode==="inbox"?"bg-white shadow-sm":"text-slate-500")}>Inbox operativa</button>
    </div>

    {mode === "chat" ? <div className="grid lg:grid-cols-[360px_minmax(0,1fr)] gap-4">
      <div className="bg-white border rounded-xl overflow-hidden">
        <div className="p-3 border-b"><div className="relative"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400"/><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca chat o cantiere…" className="w-full border rounded-lg py-2 pl-9 pr-3 text-sm"/></div></div>
        <div className="max-h-[70vh] overflow-y-auto divide-y">
          {filteredChannels.map((ch)=><button key={ch.chat_id} onClick={()=>setSelectedChat(ch.chat_id)} className={"w-full p-3 text-left hover:bg-slate-50 "+(selectedChat===ch.chat_id?"bg-emerald-50":"")}>
            <div className="flex items-center gap-2"><div className="font-semibold text-sm truncate flex-1">{ch.chat_name||ch.chat_id}</div>{ch.unread_count>0&&<span className="text-[10px] rounded-full bg-emerald-600 text-white px-2 py-0.5">{ch.unread_count}</span>}</div>
            <div className="text-[11px] text-slate-400 mt-1 truncate">{[(ch.site_names||[]).length ? "Cantieri: "+ch.site_names.join(", ") : "", ch.company_communications ? "Comunicazioni società" : ""].filter(Boolean).join(" · ") || "—"} · {ch.is_group?"Gruppo":"Chat"}</div>
          </button>)}
          {!filteredChannels.length && <div className="p-6 text-center text-sm text-slate-400">Nessuna chat WhatsApp selezionata in Impostazioni → Integrazioni.</div>}
        </div>
      </div>

      <div className="bg-white border rounded-xl overflow-hidden min-h-[520px]">
        {!selected ? <div className="h-full min-h-[520px] flex items-center justify-center text-slate-400">Seleziona una chat.</div> : <>
          <div className="p-4 border-b flex flex-col sm:flex-row sm:items-center gap-3">
            <div className="flex-1 min-w-0"><div className="font-semibold text-slate-900 truncate">{selected.chat_name||selected.chat_id}</div><div className="text-xs text-slate-400">{selected.chat_id}</div></div>
            <select value={selected.site_id||""} onChange={e=>mapChat(selected.chat_id,e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white">
              <option value="">Associa a cantiere…</option>{sites.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}
            </select>
          </div>
          <div className="p-4 space-y-2 max-h-[64vh] overflow-y-auto bg-slate-50">
            {chatBusy ? <div className="py-12 text-center text-slate-400"><Loader2 className="w-7 h-7 animate-spin mx-auto"/></div> :
            chatMessages.length===0 ? <div className="py-12 text-center text-slate-400">Nessun messaggio nella chat.</div> :
            [...chatMessages].sort((a,b)=>Number(a.timestamp||0)-Number(b.timestamp||0)).map((m,i)=><div key={m.id||i} className={"flex "+(m.fromMe?"justify-end":"justify-start")}>
              <div className={"max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm "+(m.fromMe?"bg-emerald-100 text-slate-800":"bg-white text-slate-800 border")}>
                {m.type==="image" && m.body && <img src={"data:image/jpeg;base64,"+m.body} alt="Immagine WhatsApp" className="max-w-full sm:max-w-md rounded-xl mb-2" />}
                {m.body && m.type!=="image" && <div className="whitespace-pre-wrap break-words">{m.body}</div>}
                {!m.body && <div className="text-slate-500">{m.hasMedia ? "[Allegato / media]" : "Messaggio"}</div>}
                <div className="text-[10px] text-slate-400 text-right mt-1">{fmtDate(m.timestamp)}</div>
              </div>
            </div>)}
          </div>
        </>}
      </div>
    </div> : <div className="bg-white border rounded-xl overflow-hidden">
      <div className="p-3 border-b flex flex-col lg:flex-row gap-2 lg:items-center">
        <div className="inline-flex bg-slate-100 p-1 rounded-lg">{[["tutte","Inbox"],["da_verificare","Da verificare"],["elaborate","Elaborati"]].map(([k,l])=><button key={k} onClick={()=>setTab(k)} className={"px-3 py-1.5 text-xs font-semibold rounded-md "+(tab===k?"bg-white shadow-sm":"text-slate-500")}>{l}</button>)}</div>
        <select value={siteId} onChange={e=>setSiteId(e.target.value)} className="border rounded-lg px-3 py-2 text-sm bg-white lg:ml-2"><option value="">Tutti i cantieri</option>{sites.map((s)=><option key={s.id} value={s.id}>{s.name}</option>)}</select>
        <div className="relative lg:ml-auto lg:w-96"><Search className="w-4 h-4 absolute left-3 top-2.5 text-slate-400"/><input value={q} onChange={e=>setQ(e.target.value)} placeholder="Cerca chat, testo, cantiere…" className="w-full border rounded-lg py-2 pl-9 pr-3 text-sm"/></div>
      </div>
      <div className="p-3 space-y-2">{shown.length===0?<div className="py-12 text-center text-slate-400"><Inbox className="w-8 h-8 mx-auto mb-2"/>Nessun messaggio importato. Associa prima una chat a un cantiere.</div>:shown.map((m)=>{const meta=CLASSIFICATION[m.classification]||CLASSIFICATION.altro;return <div key={m.id||m.message_id} className="rounded-xl border p-4">
        <div className="flex flex-wrap gap-2 items-center"><div className="font-semibold">{m.chat_name||"WhatsApp"}</div><span className={"text-[11px] px-2 py-0.5 rounded-full border font-semibold "+meta.cls}>{meta.label}</span></div>
        <div className="text-xs text-slate-400 mt-1">{siteById[m.site_id]?.name||"Cantiere non indicato"} · {fmtDate(m.timestamp)} · {TARGET[m.target_type]||m.target_type||"Inbox"}</div>
        {m.body&&<div className="mt-3 text-sm whitespace-pre-wrap">{m.body}</div>}
        {m.transcript&&<div className="mt-2 text-sm text-slate-600 whitespace-pre-wrap">{m.transcript}</div>}
        <div className="mt-3 flex gap-2">{m.file_id&&<a href={api.fileUrl(m.file_id)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border"><Paperclip className="w-3.5 h-3.5"/>Apri allegato</a>}{m.location?.latitude!=null&&<a href={`https://www.google.com/maps?q=${m.location.latitude},${m.location.longitude}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border"><MapPin className="w-3.5 h-3.5"/>Posizione</a>}</div>
      </div>})}</div>
    </div>}
  </div>;
}
